import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, Spin, Alert, Checkbox, Space, Modal } from 'antd';
import DocumentTitle from 'react-document-title';
import './index.less';
import { EyeInvisibleOutlined, EyeTwoTone, UserOutlined, LockOutlined, CloudDownloadOutlined } from '@ant-design/icons';
import Cookies from 'js-cookie';
import HeroLogin from './HeroLogin';
import EnDeAES from 'helpers/EnDeAES';
import AuthServiceApi from 'apis/api/Auth';
import {
  TTableSetting,
  getUserCurrent,
  initialSettingTable,
  resetState,
  updateSuccessLogin,
  updateTableSettingLogin,
  updateToken
} from '@slice/user.slice';
import { batch, useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { updateActiveMenuHeader } from '@slice/select.slice';

import { RootState } from 'app/store';
import { removeHistoryNoti } from '@slice/orderManager.slice';
import { CONFIG_WEBSITE } from 'global/Global';
import { publicUrl } from '@utils/env';

const Login = () => {
  const { t } = useTranslation();
  const history = useNavigate();
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const [form] = Form.useForm();
  const [, forceUpdate] = useState({});

  const account = useSelector((state: RootState) => state.user.account);

  const buttonLogin = useRef<HTMLInputElement>(null);
  const refSaveData = useRef<boolean>(false);
  const refUsername = useRef<any>(null);
  const [loading, setIsLoading] = useState<boolean>(false);

  const [isFailLogin, setIsFailLogin] = useState<boolean>(false);
  const [messageError, setMessageError] = useState<string>('');

  const userName = Form.useWatch('userName', form);
  const passWord = Form.useWatch('passWord', form);
  const isRemember = Form.useWatch('remember', form);

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      setIsFailLogin(false);

      Cookies.set('uname', userName);
      const pwdHash = EnDeAES.aesEncrypt(passWord);
      Cookies.set('pwd', pwdHash);

      if (isRemember) {
        localStorage.setItem('saveMe', 'true');
      } else {
        localStorage.removeItem('saveMe');
      }
      await AuthServiceApi.LoginV2({
        userName: userName.trim(),
        password: passWord.trim(),
        version: CONFIG_WEBSITE.version.split('V.').join(''),
        os: 'WEB'
      }).then(async (res: any) => {
        setIsLoading(false);
        if (res.data.error === false) {
          const { accessToken, refreshToken } = res.data.data;

          // Xử lý khi login bằng 1 tk khác
          if (userName.trim() !== userName) {
            console.log('remove localstorage role');
            localStorage.removeItem('role');
            localStorage.removeItem('roleDev');
            localStorage.removeItem('persist:user');
            localStorage.removeItem('persist:select');
          }
          const tableSetting = localStorage.getItem(userName + '/TableSetting');
          const retrievedTS: TTableSetting = tableSetting
            ? (JSON.parse(tableSetting) as TTableSetting)
            : initialSettingTable;
          // console.log('table setting login', retrievedTS);
          dispatch(updateTableSettingLogin(retrievedTS));
          // Cookies.set('token', res.data.data);
          // window.sessionStorage.setItem('token', res.data.data);
          // window.sessionStorage.setItem('uName', userName.trim());

          Cookies.set('token', accessToken);
          Cookies.set('refreshToken', refreshToken); // refreshToken lưu cookie (30 ngày)
          window.sessionStorage.setItem('token', accessToken);
          window.sessionStorage.setItem('uName', userName.trim());

          // load tất cả nhũng thứ cần sau khi login xong
          const notifyC = { maDonHang: undefined, clearAll: true };
          batch(() => {
            // dispatch(updateToken(res.data.data));
            dispatch(updateToken(accessToken));
            dispatch(getUserCurrent());
            dispatch(updateSuccessLogin(true));
            dispatch(removeHistoryNoti(notifyC)); // xoá tất cả những lịch sử đơn đã lưu sau khi login xong
          });
          // setUpTokenMessage();
          history('/');
        } else {
          if (res.data.messageCode === 'IP_NOT_ALLOWED') {
            const res = await axios.get('https://geolocation-db.com/json/');
            setMessageError(
              t('Login.ErrorLogin.ErrorLockIPMessage1') + res.data.IPv4 + t('Login.ErrorLogin.ErrorLockIPMessage2')
            );
          } else if (res.data.messageCode === 'ACCOUNT_IS_LOCKED') {
            setMessageError(t('Login.ErrorLogin.ErrorLockUserMessage'));
          } else if (res.data.messageCode === 'DATA_DOES_NOT_EXIST' || res.data.messageCode === 'WRONG_PASSWORD') {
            setMessageError(t('Login.ErrorLogin.ErroruNamepwd'));
          } else if (res.data.messageCode === 'OLDVERSION') {
            setMessageError(t('Login.ErrorLogin.ErrorOldVernMessage'));
          } else {
            setMessageError(t('Login.ErrorLogin.ErrorMessage'));
          }
          setIsFailLogin(true);
        }
      });
    } catch (error) {
      setIsLoading(false);
      console.log(error);
    }
  };

  const confirmReload = useCallback(() => {
    Modal.confirm({
      title: 'Thông báo',
      icon: <CloudDownloadOutlined className="translate-y-1" />,
      content: <span className="whitespace-nowrap">{t('Login.HasNewVersion')}</span>,
      width: 500,
      okText: t('Button.OK'),
      cancelText: t('Button.Cancel'),
      onOk: () => {
        window.location.reload();
      }
    });
  }, []);

  const getData = async () => {
    await fetch(`${publicUrl}/data/versionWeb.json?initTime=${new Date().getTime()}`, {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      }
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (myJson) {
        // console.log('version compare', myJson[CONFIG_WEBSITE.keyWeb]?.version, CONFIG_WEBSITE.version);
        if (myJson[CONFIG_WEBSITE.keyWeb]?.version !== CONFIG_WEBSITE.versionDisplay) {
          confirmReload();
        }
      });
  };

  useEffect(() => {
    // dispatch(updateActiveMenuHeader(account?.defaultMenu));
    dispatch(resetState());
    forceUpdate({});
    setTimeout(() => {
      getData();
    }, 500);

    const usernameValue = Cookies.get('uname');
    const pwd = Cookies.get('pwd');
    const save = localStorage.getItem('saveMe');

    const token = Cookies.get('token');
    if (token) {
      // cần xác thực token này để các bước bên trong xác thực
      history('/');
    }

    if (save != null) {
      console.log('coi lại', usernameValue, save);
      if (usernameValue && pwd) {
        refSaveData.current = true;
        form &&
          form.setFieldsValue({
            userName: usernameValue,
            passWord: EnDeAES.aesDecrypt(pwd)!.toString(),
            remember: save
          });
      }
    }
  }, []);

  // buttonLogin
  useEffect(() => {
    if (buttonLogin.current) {
      buttonLogin.current.focus();
    }

    // if (refUsername.current) {
    //   refUsername.current.focus();
    // }
  }, []);

  return (
    <>
      <DocumentTitle title={t('Login.Title')}>
        <Spin spinning={loading} tip={t('Login.Loading')}>
          <HeroLogin>
            <Form
              form={form}
              className="content text-center min-w-[300px] max-w-[390px]shadow-xl p-[30px] pb-0"
              onFinish={handleSubmit}
            >
              <Space size={40} />

              <Form.Item
                name={'userName'}
                rules={[
                  {
                    required: true,
                    message: t('Login.ErrorInput.UserName')
                  }
                ]}
              >
                <Input
                  ref={refUsername}
                  prefix={<UserOutlined rev={''} className="text-slate-700 site-form-item-icon" />}
                  type="text"
                  placeholder={t('Login.UserName')}
                />
              </Form.Item>
              <Form.Item
                name={'passWord'}
                rules={[
                  {
                    required: true,
                    message: t('Login.ErrorInput.PassWord')
                  }
                ]}
              >
                <Input.Password
                  iconRender={visible => (visible ? <EyeTwoTone rev={''} /> : <EyeInvisibleOutlined rev={''} />)}
                  prefix={<LockOutlined rev={''} className="text-slate-700 site-form-item-icon" />}
                  type="password"
                  placeholder={t('Login.PassWord')}
                />
              </Form.Item>
              <Form.Item className="grid justify-items-start" name="remember" valuePropName="checked">
                <Checkbox className="text-white" defaultChecked={true} checked={isRemember}>
                  {t('Login.saveMe')}
                </Checkbox>
              </Form.Item>

              <Form.Item>
                <Button
                  disabled={
                    (!(form.isFieldTouched('passWord') && form.isFieldTouched('userName')) ||
                      !!form.getFieldsError().filter(({ errors }) => errors.length).length) &&
                    refSaveData.current
                  }
                  ref={buttonLogin}
                  type="primary"
                  htmlType="submit"
                  className="login-form-button w-full"
                >
                  {t('Login.Title')}
                </Button>
              </Form.Item>
              <Space size={20} />
              {messageError && (
                <Alert
                  className="max-w-[240px]"
                  message={messageError}
                  type={'error'}
                  showIcon
                  closable
                  onClose={() => setMessageError('')}
                />
              )}
            </Form>
          </HeroLogin>
        </Spin>
      </DocumentTitle>
    </>
  );
};

export default Login;
